import React, { useCallback, useMemo } from 'react';
import {
  servicesConstants, useFeatureFlagsContext, useRootContext, useServicesQuery,
} from '@jarvis/react-portal-addons';
import GenericThemeProvider from '../../../shared/GenericThemeProvider';
import {
  Container, Grid, StatusContainer, UsageContainer,
} from './styles';
import SolutionCards from '../SolutionCards';
import HPUsageDataWidget from '../../HPUsageDataWidget';
import useAccountApiCall from '../../../hooks/useAccountApiCall';
import useDeviceCacheApiCall from '../../../hooks/useDeviceCacheApiCall';
import StatusWidget from '../../StatusWidget';
import Banner from '../Banner';

const AdminConsolidatedDashboard = () => {
  const {
    dashboardUsageDataUnitFromOrg,
    consolidatedPortalUsageDataTelemetryCheck,
    consolidatedPortalCheckAdvancedViewGrant,
  } = useFeatureFlagsContext();

  const { shell, stack } = useRootContext();

  const { authProvider, localization: { country: urlCountry } } = shell;

  const currentDate = useMemo(() => new Date(Date.now()), []);

  const accountCall = useAccountApiCall({
    init: dashboardUsageDataUnitFromOrg,
  });

  const country = dashboardUsageDataUnitFromOrg ? (accountCall.data?.regionId || urlCountry) : urlCountry;

  const deviceCache = useDeviceCacheApiCall({
    authProvider,
    stack,
    init: true,
  });

  const { SERVICE_STATES, SERVICE_TYPES, SERVICE_LEVELS } = servicesConstants;
  const advancedViewQuery = useMemo(() => ({
    type: SERVICE_TYPES.GRANTS,
    level: SERVICE_LEVELS.ACCOUNT,
    services: [
      {
        id: 'advancedView',
        states: [SERVICE_STATES.ENABLED],
        serviceIds: ['solution.client.portal/advanced-view'],
      },
    ],
  }), [SERVICE_LEVELS.ACCOUNT, SERVICE_STATES.ENABLED, SERVICE_TYPES.GRANTS]);

  const { helpers: servicesHelpers, pending: servicesPending } = useServicesQuery(advancedViewQuery);

  const advancedViewIsEnabled = useCallback((defaultValue = true) => {
    const hasAdvancedView = servicesHelpers?.checkService({ id: 'advancedView' });
    return consolidatedPortalCheckAdvancedViewGrant ? hasAdvancedView : defaultValue;
  }, [consolidatedPortalCheckAdvancedViewGrant, servicesHelpers]);

  return (
    <GenericThemeProvider>
      <Container data-testid="admin-dashboard">
        {!servicesPending && (
          <Banner
            data-testid="banner-container"
            deviceCache={deviceCache}
            advancedView={advancedViewIsEnabled(false)}
          />
        )}

        <Grid>
          {!servicesPending && (
            <SolutionCards
              deviceCache={deviceCache}
              advancedView={advancedViewIsEnabled(false)}
            />
          )}

          {advancedViewIsEnabled() && (
            <React.Fragment>
              <StatusContainer data-testid="status-container">
                <StatusWidget
                  stack={stack} deviceCache={deviceCache}
                  consolidatedPortal
                />
              </StatusContainer>

              <UsageContainer data-testid="hp-usage-data-container">
                <HPUsageDataWidget
                  data-testid="addons-hp-data-usage-widget"
                  deviceCache={deviceCache}
                  currentDate={currentDate}
                  country={country}
                  consolidatedPortal
                  useTelemetryDiscoveryCheck={consolidatedPortalUsageDataTelemetryCheck}
                />
              </UsageContainer>
            </React.Fragment>
          )}
        </Grid>
      </Container>
    </GenericThemeProvider>
  );
};

export default AdminConsolidatedDashboard;
